import React, { useEffect, useState } from 'react';
import Footer from '../../components/Footer/Footer';
import Hero from '../../components/Hero/Hero';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import ScatteredCollageGallery from '../../components/ScatteredCollageGallery';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { usePageContent, useGalleryImages } from '../../hooks/useContentful';
import { ContentfulText } from '../../components/Contentful';
import { ContentfulImage } from '../../components/Contentful';
import { FilterBar, DESTINATION_OPTIONS, TYPE_OPTIONS } from '../../components/shared/FilterBar';
import './GalleryPage.css';
import { useTranslation } from 'react-i18next';

const GalleryPage: React.FC = () => {
  const [selectedDestination, setSelectedDestination] = useState<string | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);

  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('gallery');
  const { data: galleryImagesData } = useGalleryImages();
  const pageContent = pageContentData?.[0]?.fields as any;
  const allGalleryImages = galleryImagesData || [];

  // Filter gallery images based on selected filters
  const galleryImages = allGalleryImages.filter(image => {
    const imageFields = image.fields as any;
    const tags = imageFields.tags || [];
    const destination = imageFields.destination?.toLowerCase();

    // Filter by destination
    if (selectedDestination) {
      const hasDestination = tags.some((tag: string) =>
        tag.toLowerCase().includes(selectedDestination.toLowerCase())
      ) || destination?.includes(selectedDestination.toLowerCase());
      if (!hasDestination) return false;
    }

    // Filter by type
    if (selectedType) {
      const hasType = tags.some((tag: string) =>
        tag.toLowerCase().includes(selectedType.toLowerCase()) ||
        imageFields.category?.toLowerCase().includes(selectedType.toLowerCase())
      );
      if (!hasType) return false;
    }

    return true;
  });

  // Create a helper function to get dynamic alt text
  const getAltText = (fallback: string, imageIndex?: number): string => {
    if (galleryImages.length > 0 && imageIndex !== undefined && galleryImages[imageIndex]) {
      const fields = galleryImages[imageIndex].fields as any;
      return String(fields.altText || fallback);
    }
    return fallback;
  };

  // Create a helper function to get image URL
  const getImageUrl = (fallbackUrl: string, imageIndex?: number): string => {
    if (galleryImages.length > 0 && imageIndex !== undefined && galleryImages[imageIndex]) {
      const fields = galleryImages[imageIndex].fields as any;
      const imageField = fields.image as any;
      const imageUrl = imageField?.fields?.file?.url;
      return imageUrl ? `https:${imageUrl}` : fallbackUrl;
    }
    return fallbackUrl;
  };

  // Initialize all interactive features
  useEffect(() => {
    const cleanup = initializeAllFeatures();
    return cleanup;
  }, []);

const local_images = [
  {
    imageUrl : '/assets/images/gallery/gallery-01.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-02.png',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-03.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-04.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-05.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-06.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-07.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-08.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-09.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-10.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-11.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-12.png',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-13.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-14.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-15.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-16.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-17.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-18.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-19.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-20.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-21.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-22.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-23.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-24.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-25.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-26.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-27.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-28.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-29.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-30.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-31.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-32.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-33.png',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-34.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-35.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-36.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-37.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-38.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-39.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-40.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-41.jpg',
  },
  {
    imageUrl : '/assets/images/gallery/gallery-42.jpg',
  },
];

const { t } = useTranslation();

  return (
    <div className="gallery-page">


        <title>{t('meta:gallery.title')}</title>
        <meta name="description" content={t('meta:gallery.description')} />
        <meta property="og:title" content={t('meta:gallery.title')} />
        <meta property="og:description" content={t('meta:gallery.description')} />


      {/* Navigation */}
      <SmartNavigation
        defaultVariant="transparent"
        scrolledVariant="light"
        autoSwitchOnScroll={true}
      />

      {/* Hero Section - Figma Masthead Pattern */}
      <Hero
        backgroundImage = '/assets/images/gallery/gallery-hero.jpg'
        heading = {t('pages/gallery:heroText')}
      />

      {/* Gallery Content Text - Page Header Pattern */}
      <div className="gallery-content-text">
        <h2>
          <ContentfulText
            content={t('pages/gallery:pageDescription')}
            fallback="Placerat purus donec sagittis orci consectetur lobortis viverra."
          />
        </h2>
      </div>

      {/* Filters Section - Moved below text per Bug #33 */}
      <FilterBar
        className="gallery-filters"
        dropdowns={[
          {
            id: 'destination',
            type: 'destination',
            level: 'brand',
            options: DESTINATION_OPTIONS,
            selectedValue: selectedDestination,
            onSelect: (value: string) => setSelectedDestination(value === selectedDestination ? undefined : value)
          },
          {
            id: 'type',
            type: 'type',
            level: 'brand',
            options: TYPE_OPTIONS,
            selectedValue: selectedType,
            onSelect: (value: string) => setSelectedType(value === selectedType ? undefined : value)
          }
        ]}
        alignment="center"
      />

      <div className="gallery_list">
        {
          local_images.map((image, index) => (
            <div className="gallery__item" key={index}>
              <img src={image.imageUrl} className="gallery__image" />
            </div>
          ))
        }
      </div>

      {/* Main Gallery Container System - Enable viewport breakout for all patterns */}
      {/* Spacing Rules: Full-width → Scattered: 120px | Scattered → Triptych: 80px | Triptych → Diptych: 100px | Diptych → Window: 120px | Window → Full-width: 160px */}
      <div className="gallery-container" data-scroll-section="gallery">

        {/* Pattern 1: Full-Width Image (1440px × 840px) */}
        <div className="gallery-full-width">
          <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.topImage} />
        </div>

        {/* Pattern 2: Scattered Collage - Clean Layout */}
        <ScatteredCollageGallery
          images={{
            topLeft: images.galleryPage.figma.image6,
            rightCenter: images.galleryPage.figma.image8,
            bottomRight: images.galleryPage.figma.image3
          }}
        />

        {/* Pattern 3: Triptych - 3 Equal Columns */}
        <div className="gallery-triptych">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image3} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image5} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image6} />
          </div>
        </div>

        {/* Pattern 4: Full Width Image */}
        <div className="gallery-full-width">
          <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.fullImage4} />
        </div>

        {/* Pattern 5: Diptych - 2 Equal Columns */}
        <div className="gallery-diptych">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image9} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image10} />
          </div>
        </div>

        {/* Pattern 6: Scattered Collage 2 - Different Arrangement */}
        <ScatteredCollageGallery
          images={{
            topLeft: images.galleryPage.figma.image7,
            rightCenter: images.galleryPage.figma.image6,
            bottomRight: images.galleryPage.figma.image8
          }}
        />

        {/* Pattern 7: Triptych 2 */}
        <div className="gallery-triptych">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image3} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image15} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image14} />
          </div>
        </div>

        {/* Pattern 8: Full Width Image 2 */}
        <div className="gallery-full-width">
          <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.fullImage5} />
        </div>

        {/* Pattern 9: Window Gallery - 2x2 Grid */}
        <div className="gallery-window">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image1} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.window2} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.window3} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image4} />
          </div>
        </div>

        {/* Pattern 10: Full Width Image 3 */}
        <div className="gallery-full-width">
          <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image1} />
        </div>

        {/* Pattern 11: Scattered Collage 3 */}
        <ScatteredCollageGallery
          images={{
            topLeft: images.galleryPage.figma.image13,
            rightCenter: images.galleryPage.figma.image12,
            bottomRight: images.galleryPage.figma.image11
          }}
        />

        {/* Pattern 12: Triptych 3 */}
        <div className="gallery-triptych">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image3} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image5} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image6} />
          </div>
        </div>

        {/* Pattern 13: Scattered Collage 4 */}
        <ScatteredCollageGallery
          images={{
            topLeft: images.galleryPage.figma.image7,
            rightCenter: images.galleryPage.figma.image6,
            bottomRight: images.galleryPage.figma.image8
          }}
        />

        {/* Pattern 14: Diptych 2 */}
        <div className="gallery-diptych">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image9} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image10} />
          </div>
        </div>

        {/* Pattern 15: Full Width Image 4 */}
        <div className="gallery-full-width">
          <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.fullImage4} />
        </div>

        {/* Pattern 16: Triptych 4 */}
        <div className="gallery-triptych">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image3} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image5} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image6} />
          </div>
        </div>

        {/* Pattern 17: Full Width Image 5 */}
        <div className="gallery-full-width">
          <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.fullImage5} />
        </div>

        {/* Pattern 18: Window Gallery 2 */}
        <div className="gallery-window">
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image1} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image9} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image2} />
          </div>
          <div className="gallery-item">
            <ContentfulImage imageKey="gallery-main" fallbackSrc={images.galleryPage.figma.image4} />
          </div>
        </div>

      </div>

      {/* Instagram Section */}
      <InstagramSection
        posts={[
          { src: images.galleryPage.figma.instagram1, alt: getAltText("Instagram 1", 100) },
          { src: images.galleryPage.figma.instagram2, alt: getAltText("Instagram 2", 101) },
          { src: images.galleryPage.figma.instagram3, alt: getAltText("Instagram 3", 102) },
          { src: images.galleryPage.figma.instagram4, alt: getAltText("Instagram 4", 103) }
        ]}
      />

      {/* Footer */}
      <Footer variant="default" />
    </div>
  );
};

export default GalleryPage;
